import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  Packer,
  ShadingType,
  convertInchesToTwip,
} from 'docx';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirrors ChargeHistoryTable)
// ─────────────────────────────────────────────────────────────────────────────

interface ConvictionRow {
  date: string;
  charge: string;
  disposition: string;
  sentence?: string;
}

interface ArrestRow {
  date: string;
  charge: string;
  disposition: string;
}

interface FtaRow {
  date: string;
}

export interface ChargeHistoryData {
  defendantName: string;
  aliases: string[];
  currentArrestDate: string;
  convictions: ConvictionRow[];
  arrests: ArrestRow[];
  ftas: FtaRow[];
  reviewNotes: string[];
  rawEntriesProcessed: number;
  duplicatesRemoved: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const FONT = 'Arial';
const FONT_SIZE = 22; // half-points, so 22 = 11pt

const tableBorders = {
  top: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  bottom: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  left: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
  right: { style: BorderStyle.SINGLE, size: 4, color: '999999' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper functions
// ─────────────────────────────────────────────────────────────────────────────

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: true,
        font: FONT,
        size: 24, // 12pt
        color: '1a1a1a',
      }),
    ],
    spacing: { before: 300, after: 120 },
  });
}

function bodyText(text: string, bold = false): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        font: FONT,
        size: FONT_SIZE,
        bold,
      }),
    ],
    spacing: { after: 60 },
  });
}

function labelValue(label: string, value: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({ text: `${label}: `, font: FONT, size: FONT_SIZE, bold: true }),
      new TextRun({ text: value || '____________________', font: FONT, size: FONT_SIZE }),
    ],
    spacing: { after: 80 },
  });
}

function headerCell(text: string): TableCell {
  return new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text, font: FONT, size: 20, bold: true, color: '333333' })],
      }),
    ],
    shading: { type: ShadingType.SOLID, color: 'E8E8E8' },
    borders: tableBorders,
  });
}

function dataCell(text: string): TableCell {
  return new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text, font: FONT, size: 20 })],
      }),
    ],
    borders: tableBorders,
  });
}

function dividerLine(): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: '═'.repeat(70),
        font: 'Courier New',
        size: 20,
        color: '666666',
      }),
    ],
    spacing: { before: 200, after: 200 },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main generator
// ─────────────────────────────────────────────────────────────────────────────

export async function generateChargeHistoryDocx(data: ChargeHistoryData): Promise<void> {
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.75),
              bottom: convertInchesToTwip(0.75),
              left: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
            },
          },
        },
        children: [
          // ═══════════════════════════════════════════════════════════════════
          // HEADER
          // ═══════════════════════════════════════════════════════════════════
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'CHARGE HISTORY SUMMARY',
                bold: true,
                font: FONT,
                size: 32, // 16pt
              }),
            ],
            spacing: { after: 60 },
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'Prepared by Prosecutor\'s Office',
                font: FONT,
                size: 22,
                italics: true,
                color: '555555',
              }),
            ],
            spacing: { after: 200 },
          }),

          dividerLine(),

          // ═══════════════════════════════════════════════════════════════════
          // DEFENDANT INFORMATION
          // ═══════════════════════════════════════════════════════════════════
          sectionHeading('DEFENDANT INFORMATION'),
          labelValue('Name', data.defendantName),
          labelValue('DOB', '[REDACTED]'),
          labelValue('SID', ''),
          labelValue('CBF/Case #', ''),
          labelValue('Aliases', data.aliases.length > 0 ? data.aliases.join(', ') : 'None'),

          // ═══════════════════════════════════════════════════════════════════
          // PRIOR CONVICTIONS
          // ═══════════════════════════════════════════════════════════════════
          sectionHeading(`PRIOR CONVICTIONS (${data.convictions.length})`),

          ...(data.convictions.length > 0
            ? [
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: [
                    new TableRow({
                      children: [
                        headerCell('Date'),
                        headerCell('Charge'),
                        headerCell('Sentence'),
                      ],
                    }),
                    ...data.convictions.map(
                      (c) =>
                        new TableRow({
                          children: [
                            dataCell(c.date),
                            dataCell(`${c.charge}${c.disposition === 'PBJ' ? ' (PBJ)' : ''}`),
                            dataCell(c.sentence || '—'),
                          ],
                        })
                    ),
                  ],
                }),
              ]
            : [bodyText('None', false)]),

          // ═══════════════════════════════════════════════════════════════════
          // ARRESTS WITHOUT CONVICTION
          // ═══════════════════════════════════════════════════════════════════
          sectionHeading(`ARRESTS WITHOUT CONVICTION (${data.arrests.length})`),

          ...(data.arrests.length > 0
            ? [
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: [
                    new TableRow({
                      children: [
                        headerCell('Date'),
                        headerCell('Charge'),
                        headerCell('Disposition'),
                      ],
                    }),
                    ...data.arrests.map(
                      (a) =>
                        new TableRow({
                          children: [dataCell(a.date), dataCell(a.charge), dataCell(a.disposition)],
                        })
                    ),
                  ],
                }),
              ]
            : [bodyText('None', false)]),

          // ═══════════════════════════════════════════════════════════════════
          // FTAs
          // ═══════════════════════════════════════════════════════════════════
          sectionHeading(`⚠ FAILURES TO APPEAR (within 3 years)`),

          ...(data.ftas.length > 0
            ? [bodyText(data.ftas.map((f) => f.date).join('    '))]
            : [bodyText('None within 3-year window', false)]),

          // ═══════════════════════════════════════════════════════════════════
          // PROSECUTOR'S NARRATIVE (blank for human entry)
          // ═══════════════════════════════════════════════════════════════════
          sectionHeading("PROSECUTOR'S NARRATIVE"),
          new Paragraph({
            children: [
              new TextRun({
                text: '(Brief chronological overview — to be completed by prosecutor)',
                font: FONT,
                size: 20,
                italics: true,
                color: '888888',
              }),
            ],
            spacing: { after: 100 },
          }),
          // Blank lines for handwriting/typing
          bodyText(''),
          bodyText(''),
          bodyText(''),
          bodyText(''),
          bodyText(''),

          dividerLine(),

          // ═══════════════════════════════════════════════════════════════════
          // FOOTER / SIGNATURE BLOCK
          // ═══════════════════════════════════════════════════════════════════
          new Paragraph({
            children: [
              new TextRun({ text: 'Prepared By: ', font: FONT, size: FONT_SIZE, bold: true }),
              new TextRun({ text: '_________________________    ', font: FONT, size: FONT_SIZE }),
              new TextRun({ text: 'Date: ', font: FONT, size: FONT_SIZE, bold: true }),
              new TextRun({ text: today, font: FONT, size: FONT_SIZE }),
            ],
            spacing: { after: 200 },
          }),

          // Verification checkboxes
          bodyText('☐  Verified against source RAP sheet'),
          bodyText('☐  Date types confirmed (disposition date for Guilty/PBJ)'),
          new Paragraph({
            children: [
              new TextRun({ text: '☐  Duplicate entries removed: ', font: FONT, size: FONT_SIZE }),
              new TextRun({ text: String(data.duplicatesRemoved), font: FONT, size: FONT_SIZE, bold: true }),
            ],
            spacing: { after: 200 },
          }),

          // Generated by notice
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'Generated by DEMS — for official use only',
                font: FONT,
                size: 18,
                italics: true,
                color: '888888',
              }),
            ],
            spacing: { before: 200 },
          }),
        ],
      },
    ],
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const safeName = data.defendantName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `Charge_History_${safeName}_${today.replace(/\//g, '-')}.docx`;
  
  // Native browser download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
